/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { MENU_HEIGHT } from 'src/components/css'
import useWindowSize from 'src/hooks/useWindowSize'
import { useOverlayTransition } from 'src/hooks/useOverlayTransition'
import { useSidebarState } from 'src/hooks/useSidebarState'

const TRANSITION_DURATION = 300
const MOBILE_BREAKPOINT = 768

export interface FullScreenOverlayProps {
  isOpen: boolean
  children: React.ReactNode
}

/**
 * A full-screen page laid over the app content: below the top menu, beside the
 * left sidebar (following its collapsed width), fading + sliding in. Owns the
 * mount lifecycle and the page scroll lock — children mount only while open.
 *
 * Pair with `FullScreenHeader` for the BACK link, title and header actions.
 * Used by Edit Player and Game Attendance.
 */
const FullScreenOverlay: React.FC<FullScreenOverlayProps> = ({
  isOpen,
  children,
}) => {
  const { isMounted, isVisible } = useOverlayTransition(
    isOpen,
    TRANSITION_DURATION
  )
  const { width } = useWindowSize()
  const isMobile = width <= MOBILE_BREAKPOINT
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284

  if (!isMounted) {
    return null
  }

  return (
    <div
      css={css`
        ${overlayStyle}
        top: ${isMobile ? 48 : MENU_HEIGHT}px;
        left: ${sidebarWidth}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${TRANSITION_DURATION}ms ease-out,
          transform ${TRANSITION_DURATION}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>{children}</div>
    </div>
  )
}

export { default as FullScreenHeader } from './FullScreenHeader'
export type { FullScreenHeaderProps } from './FullScreenHeader'
export default FullScreenOverlay

// ---- styled ----
const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 15;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: none;
    padding: 0;
  }
`
